import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import { useDispatch, useSelector } from 'react-redux';
import ReactHtmlParser from 'react-html-parser';
import { Checkbox, Icon } from '@BICE_Vida/components';
import { useMutation } from '@apollo/client';
import { v1 as uuid } from 'uuid';
import Cookies from 'universal-cookie';

import { useIsAssisted } from '@/src/customHooks';
import Text from '../../designSystem/Text';
import Modal from '@/designSystem/Modal';

import styles from './PymeDataForm.module.scss';

import {
  authorizationHoldingTexts,
  modalFailedTexts,
  personalInfoStepTexts,
} from '@/utils/generalTexts';
import {
  formHireValidationSchema,
  getFormValidation,
  userAndCompanyDataValidation,
} from '@/utils/validations';
import {
  leadOrigin,
  maxLengthRut,
  OriginTypes,
  TIME_TO_EXPIRE,
  wizardSummaryStep,
} from '@/utils/constants';

import * as wizardActions from '@/redux/wizard/actions';
import * as wizardSelectors from '@/redux/wizard/selectors';
import * as hiringActions from '@/redux/hiring/actions';
import * as persistence from '@/utils/persistence';

import componentsId from '@/utils/componentsId';
import AuthorizationModal from '../AuthorizationModal';
import { formatRut, splitFullName, unformatRut } from '@/utils/format';
import {
  clickActionsEvents,
  quotationFlowEvents,
  trackPageViewQuotation,
} from '@/utils/dataLayer/quotationFlow';
import { setExtraFormData } from '../../redux/wizard/actions';
import createLead from '@/graphQL/mutations/createLead';
import createCompany from '@/graphQL/mutations/createCompany';
import CompanyInfoForm from '../UserAndCompanyStep/CompanyInfoForm';
import PersonalInfoForm from '../UserAndCompanyStep/PersonalInfoForm';
import routes from '@/utils/routes';

function PymeDataForm() {
  const dispatch = useDispatch();
  const router = useRouter();
  const cookies = new Cookies();
  const isAssisted = useIsAssisted();

  const userData = useSelector(wizardSelectors.getUserAndCompanyData);
  const getDataLayerInfo = useSelector(wizardSelectors.getDataLayerInfo);
  const extraFormData = useSelector(wizardSelectors.getExtraFormData);
  const { email: executiveEmail } = useSelector(wizardSelectors.getExecutive);
  const planSelected = useSelector(wizardSelectors.getPlanSelected);
  const workersSelected = useSelector(wizardSelectors.getWorkersSelected);
  const variants = useSelector(wizardSelectors.getFinalVariants);
  const totalCompany = useSelector(wizardSelectors.getTotalCompany);
  const sendLead = useSelector(wizardSelectors.getSendLeadOrder);
  const businessData = useSelector(wizardSelectors.getBusiness);

  const [checked, setChecked] = useState(false);
  const [formWithErrors, setFormWithErrors] = useState(false);

  const [availableInput, setAvailableInput] = useState({
    companyName: false,
    form: true,
  });

  const [userAndCompanyForm, setUserAndCompanyForm] = useState({
    contactName: userData.name || userData.lastname ? `${userData.name} ${userData.lastname}` : '',
    companyName: userData.companyName,
    companyRut: userData.companyRut,
    name: userData.name,
    lastname: userData.lastname,
    email: userData.email,
    phone: userData.phone ?? '',
    assistCode: extraFormData.assistCode ?? '',
  });

  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [textsModal, setTextsModal] = useState({
    id: '',
    textTitle: '',
    textDescription: '',
    idButton: '',
    textButton: '',
    imgModal: '' as React.ComponentProps<typeof Modal>['img'],
    action: '',
  });

  const [authModalIsOpen, setAuthModalIsOpen] = useState(false);

  const [formErrors, setFormErrors] = useState({
    companyRut: { hasError: false, feedback: '' },
    contactName: { hasError: false, feedback: '' },
    companyName: { hasError: false, feedback: '' },
    email: { hasError: false, feedback: '' },
    referCode: { hasError: false, feedback: '' },
    assistCode: { hasError: false, feedback: '' },
    phone: { hasError: false, feedback: '' },
  });

  const [createCompanyMutation] = useMutation(createCompany);
  const [createLeadMutation] = useMutation(createLead);

  useEffect(() => {
    const feedback = getFormValidation(userAndCompanyForm, formErrors, formHireValidationSchema);
    if (feedback?.companyRut.hasError) {
      setAvailableInput({ companyName: false, form: true });
      setFormErrors({
        ...formErrors,
        companyRut: { hasError: false, feedback: '' },
      });
    }
  }, [userAndCompanyForm.companyRut]);

  useEffect(() => {
    (async () => {
      try {
        await userAndCompanyDataValidation(isAssisted).validateAsync(userAndCompanyForm, {
          convert: false,
        });

        dispatch(
          wizardActions.setUserAndCompanyData({
            ...userData,
            name: userAndCompanyForm.name.trim(),
            lastname: userAndCompanyForm.lastname.trim(),
            email: userAndCompanyForm.email.trim(),
            phone: userAndCompanyForm.phone.trim(),
            companyName: userAndCompanyForm.companyName.trim(),
            companyRut: userAndCompanyForm.companyRut.trim(),
          })
        );
        dispatch(wizardActions.setUserAndCompanyValidation(true));
      } catch (error) {
        dispatch(wizardActions.setUserAndCompanyValidation(false));
      }
    })();
  }, [userAndCompanyForm, isAssisted]);

  const handleChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const { id } = event.target;
    let { value } = event.target;

    setUserAndCompanyForm({
      ...userAndCompanyForm,
      [id]: value,
    });

    if (id === componentsId.formStep.inputContactName) {
      const { name, lastname } = splitFullName(value);
      setUserAndCompanyForm({
        ...userAndCompanyForm,
        contactName: value,
        name,
        lastname,
      });

      setFormErrors({
        ...formErrors,
        [componentsId.formStep.inputContactName]: {
          hasError: false,
          feedback: '',
        },
      });

      return;
    }

    if (id === componentsId.formStep.inputCompanyRut) {
      value = formatRut(unformatRut(value).slice(0, maxLengthRut));
      setUserAndCompanyForm({
        ...userAndCompanyForm,
        companyRut: value,
      });
      setFormErrors({
        ...formErrors,
        [componentsId.formStep.inputCompanyRut]: {
          hasError: false,
          feedback: '',
        },
      });

      return;
    }

    if (id === componentsId.formStep.assistCode) {
      value = value.length < 3 ? value : formatRut(unformatRut(value).slice(0, maxLengthRut));
      dispatch(
        setExtraFormData({
          ...extraFormData,
          assistCode: value,
        })
      );
    }

    try {
      await userAndCompanyDataValidation(isAssisted).validateAsync(
        { [id]: value },
        { convert: false }
      );

      if (formErrors[id as keyof typeof formErrors].hasError) {
        setFormErrors({
          ...formErrors,
          [id]: {
            hasError: false,
            feedback: '',
          },
        });
      }
    } catch (error) {
      //
    }
  };

  const handleBlur = async (event: any) => {
    const { id, value } = event.target;
    try {
      await userAndCompanyDataValidation(isAssisted).validateAsync(
        { [id]: value },
        { convert: false }
      );
      setFormErrors({
        ...formErrors,
        [id]: {
          hasError: false,
          feedback: '',
        },
      });
    } catch (error) {
      setFormErrors({
        ...formErrors,
        [id]: {
          hasError: true,
          feedback: (error as Error).message,
        },
      });
    }
  };

  const handleAuthModal = () => {
    setAuthModalIsOpen(true);
    setChecked(!checked);
  };

  useEffect(() => {
    const handleModal = document.getElementById('holding_authorization_link');
    handleModal?.addEventListener('click', handleAuthModal);
    handleModal?.addEventListener('click', () => setAuthModalIsOpen(true));

    return () => {
      handleModal?.removeEventListener('click', handleAuthModal);
      handleModal?.removeEventListener('click', () => setAuthModalIsOpen(true));
    };
  }, []);

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>, regEx: RegExp) => {
    if (!regEx.test(event.key)) {
      event.preventDefault();
    }
  };

  const onCloseModalHandler = () => setAuthModalIsOpen(false);

  const handlerCheck = (check: boolean) => {
    setChecked(check);
    dispatch(
      wizardActions.setExtraFormData({
        ...extraFormData,
        holdingAuthorization: check,
      })
    );
  };

  const handleCloseModal = async () => {
    setModalIsOpen(false);

    if (!formWithErrors) {
      await persistence.destroy();
      router.push(routes.landing);
    }
  };

  const formatRutOnBlur = (event: any) => {
    if (event.target.value.length) {
      event.target.value = formatRut(unformatRut(event.target.value).slice(0, maxLengthRut));
    }
  };

  const handleForm = async () => {
    trackPageViewQuotation({
      eventParams: {
        ...quotationFlowEvents.companyStep,
        ...getDataLayerInfo.event_params,
        action_type: 'click',
        action: clickActionsEvents.formStepGetQuote,
      },
      user: getDataLayerInfo.user,
    });

    try {
      const executiveLeadInput = executiveEmail ? { email: executiveEmail } : undefined;

      const createResult = await createLeadMutation({
        variables: {
          input: {
            ...userData,
            workers: workersSelected,
            planId: planSelected,
            variants,
            totalCompany: {
              uf: parseFloat(totalCompany.toFixed(2)),
            },
            phone: Number(userData.phone.replace(/ /g, '')),
            business: businessData || leadOrigin,
            ...extraFormData,
            position: ' ',
            management: {
              origin: OriginTypes.MIPYMESEGURA,
            },
          },
          executiveLeadInput,
        },
      });

      cookies.set('x-amzn-client', createResult.data.createLead.client, {
        maxAge: TIME_TO_EXPIRE,
        secure: true,
        sameSite: true,
        path: '/',
      });

      const companyData = [
        {
          id: uuid(),
          rut: userAndCompanyForm.companyRut,
          name: userAndCompanyForm.companyName,
          activity: '',
          phone: '',
          region: '',
          district: '',
          address: '',
          primary: true,
        },
      ];

      await createCompanyMutation({
        variables: {
          input: {
            leadId: createResult.data.createLead._id,
            subCompanies: companyData,
          },
        },
      });

      setFormWithErrors(false);
      dispatch(wizardActions.resetSendLead());
      dispatch(wizardActions.resetFormValidation());
      dispatch(hiringActions.setEmailPersist(userData.email));
      dispatch(hiringActions.setSubCompanies(companyData));
      dispatch(hiringActions.setLeadCreated(createResult.data.createLead._id));

      dispatch(wizardActions.setCurrentStep(wizardSummaryStep));
      router.push(routes.wizardSummary);
    } catch (error) {
      dispatch(wizardActions.setSendLead(false));
      setTextsModal({
        ...textsModal,
        id: componentsId.modalErrorHire,
        textTitle: modalFailedTexts.title,
        textDescription: modalFailedTexts.description,
        idButton: componentsId.modalErrorHireBtn,
        textButton: modalFailedTexts.button,
        imgModal: 'formModalFail',
      });
      setFormWithErrors(true);
      setModalIsOpen(true);
    }
  };

  useEffect(() => {
    if (sendLead) {
      handleForm();
    }
  }, [sendLead]);

  return (
    <div className={styles.pyme_form_container}>
      <Modal
        id={textsModal.id}
        isOpen={modalIsOpen}
        img={textsModal.imgModal}
        textTitle={textsModal.textTitle}
        textDescription={textsModal.textDescription}
        button={{
          id: textsModal.idButton,
          text: textsModal.textButton,
          click: () => {
            handleCloseModal();
          },
          disabled: false,
        }}
      />

      <div className={styles.pyme_form_title}>{personalInfoStepTexts.pymeFormTitle}</div>

      <div className={styles.pyme_form_fields}>
        <CompanyInfoForm
          companyRut={userAndCompanyForm.companyRut}
          companyName={userAndCompanyForm.companyName}
          formErrors={formErrors}
          availableInput={availableInput}
          loading={false}
          handleChange={handleChange}
          handleBlur={handleBlur}
          formatRutOnBlur={formatRutOnBlur}
          handleKeyPress={handleKeyPress}
        />
        <PersonalInfoForm
          personalInfoForm={userAndCompanyForm}
          formErrors={formErrors}
          handleChange={handleChange}
          handleBlur={handleBlur}
          handleKeyPress={handleKeyPress}
        />
      </div>

      <div className={styles.pyme_form_info}>
        <Icon name="info" variant="secondary" />
        <Text customClass={styles.pyme_form_info_text} text={personalInfoStepTexts.description} />
      </div>

      <AuthorizationModal isOpen={authModalIsOpen} onClose={onCloseModalHandler} />

      <div className={styles.pyme_form_checkbox}>
        <Checkbox
          id={componentsId.holdingAuthorizationCheckbox}
          onChange={handlerCheck}
          checked={checked}
          className={styles.checkbox_conditions}
          text={ReactHtmlParser(authorizationHoldingTexts.message())}
        />
      </div>
    </div>
  );
}

export default PymeDataForm;
